import { useState } from 'react'
import { cn } from '@/lib/cn'
import { Banner, Field, TextArea } from '@/ui'
import { errorMessage } from '../../api/core'
import { useVerifyLabReport } from '../../api/lab'
import { validateVerify, type VerifyDraft } from '../../model/lab/labReportForm'
import { FormDialog } from '../FormDialog'

/** Second-person check of a lab report's rows against its PDF (F7.5-f). */
export function VerifyReportDialog({
  reportId,
  reportNo,
  initialDecision,
  blockedReason,
  onClose,
}: {
  reportId: string
  reportNo: string
  initialDecision: 'VERIFIED' | 'REJECTED'
  /** E.g. the person who entered it. */
  blockedReason: string | null
  onClose: () => void
}) {
  const [d, setD] = useState<VerifyDraft>({ decision: initialDecision, note: '' })
  const [touched, setTouched] = useState(false)
  const verify = useVerifyLabReport()
  const p = validateVerify(d)
  const ok = !p.decision && !p.note && !blockedReason

  return (
    <FormDialog
      open
      onClose={onClose}
      title={`Check report ${reportNo}`}
      description="Open the PDF beside the rows. Verify when every row matches the PDF; reject with a note when one does not."
      saveLabel={d.decision === 'REJECTED' ? 'Reject report' : 'Verify report'}
      tone={d.decision === 'REJECTED' ? 'danger' : 'primary'}
      loading={verify.isPending}
      canSave={!blockedReason}
      onSave={async () => {
        setTouched(true)
        if (!ok || !d.decision) throw new Error('invalid')
        const note = d.note.trim()
        await verify.mutateAsync({ id: reportId, decision: d.decision, ...(note ? { note } : {}) })
      }}
    >
      {blockedReason && (
        <p role="alert" className="rounded-card border border-warn/30 bg-warn-wash px-3 py-2 text-[14px] text-ink">
          {blockedReason}
        </p>
      )}
      {verify.isError && (
        <Banner tone="error" title="Not saved">
          {errorMessage(verify.error)}
        </Banner>
      )}
      <fieldset className="grid grid-cols-2 gap-2">
        <legend className="sr-only">Decision</legend>
        {(['VERIFIED', 'REJECTED'] as const).map((k) => (
          <label
            key={k}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-chip border px-3 py-2 text-[14px]',
              d.decision === k ? 'border-ink bg-paper-tint' : 'border-rule',
            )}
          >
            <input type="radio" name="verify" checked={d.decision === k} onChange={() => setD({ ...d, decision: k })} />
            {k === 'VERIFIED' ? 'Rows match the PDF' : 'Reject'}
          </label>
        ))}
      </fieldset>
      <Field
        label="Note"
        required={d.decision === 'REJECTED'}
        error={touched ? p.note : undefined}
        hint={d.decision === 'REJECTED' ? 'What is wrong, e.g. row 12 typed 0.3 instead of 0.03.' : 'Optional.'}
      >
        <TextArea value={d.note} maxLength={300} onChange={(e) => setD({ ...d, note: e.target.value })} rows={3} />
      </Field>
    </FormDialog>
  )
}
